import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled, { keyframes, useTheme } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import type { ImageCarouselInterstitial, ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import { getImageAtCarouselIndex } from 'LuxKit/Carousel/imageGalleryUtils'

import { accountAccessShowLogin } from 'actions/UiActions'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray, min } from 'lib/array/arrayUtils'
import { isBedbankOffer, isBundleOffer, isFlashHotelOffer, isLEHotel, isLpcHotelOffer, isVillaOffer } from 'lib/offer/offerTypes'
import { buildRoomPackageOptionsViewOrder, buildRoomPackageOptionsViewOrderLPC, getPriceDisplayFunction, hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { getThemeClassName } from 'lib/theme/themeUtils'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { hasPassedHidePricing, selectLoggedIn } from 'selectors/accountSelectors'
import {
  getAvailableDurationPackageOptions,
  getDurationPackageOptions,
  getPropertyCombinedRatesByRoomIfOffer,
  getPropertyRoomOptions,
} from 'selectors/offerPage/offerPageSelectors'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import OfferGalleryPropertyImages, { PropertyImageButton } from './OfferGalleryPropertyImages'
import OfferGallerySectionedSidebar from './OfferGallerySectionedSidebar'
import OfferGallerySectionedSingleView from './OfferGallerySectionedSingleView'

const SectionedGalleryModal = styled(ModalBase)`
  border-radius: 0;
`

const SectionedGalleryWrapper = styled.div`
  display: contents;
`

const SectionedGalleryLayout = styled.div`
  grid-area: modal-body;
  width: 100%;
  display: grid;
  grid-template-columns: ${rem(280)} 1fr;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const SkeletonPulse = keyframes`
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
`

const SkeletonBlock = styled.div`
  background: ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(8)};
  animation: ${SkeletonPulse} 1.4s ease-in-out infinite;
`

const SkeletonLine = styled(SkeletonBlock)`
  height: ${rem(10)};
  width: 100%;
`

const ContentArea = styled.div`
  overflow-y: auto;
  min-height: 0;
  padding: ${rem(24)};

  > * + * {
    margin-top: ${rem(40)};
  }
`

const Section = styled.section`
  scroll-margin-top: ${rem(24)};

  > * + * {
    margin-top: ${rem(16)};
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${rem(16)};
`

const SectionCount = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const PropertyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};
  margin-top: ${rem(16)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const SectionSkeletonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${rem(12)};
`

const SkeletonGridItem = styled(SkeletonBlock)`
  aspect-ratio: 4 / 3;
`

const RoomGroup = styled.div`
  display: grid;
  gap: ${rem(12)};
`

const RoomLargeImageButton = styled(PropertyImageButton)`
  grid-column: 1 / -1;
  aspect-ratio: 16 / 9;
  position: relative;
`

const RoomSmallRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${rem(12)};
`

const RoomOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${rem(80)} ${rem(16)} ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(8, 8, 8, 0.85) 100%);
  backdrop-filter: blur(${rem(10)});
  mask-image: linear-gradient(to bottom, transparent, black ${rem(80)});
  -webkit-mask-image: linear-gradient(to bottom, transparent, black ${rem(80)});
`

const RoomOverlayTitle = styled(Heading)`
  color: inherit;
`

const RoomOverlayDescription = styled(BodyText)`
  color: inherit;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const RoomOverlayPriceCaption = styled(PriceRowPriceCaption)`
  color: inherit;
`

const RoomOverlayPriceStackWrapper = styled.div`
  color: inherit;
`

const PROPERTY_SECTION_ID = 'property'
const BEDBANK_RICH_DESC_REGEX = /^\*\*/m

type ViewMode = 'sections' | 'single'

interface RoomSection {
  id: string
  name: string
  images: Array<App.Image>
  thumbnail?: App.Image
}

export interface GallerySection {
  id: string
  navLabel: string
  heading: string
  images: Array<App.Image>
  thumbnail?: App.Image
  type: 'property' | 'room'
  largeImage?: App.Image
  smallImages?: Array<App.Image>
  countLabel: string
}

interface RoomOverlayDetails {
  name: string
  description?: string
  price?: number
  memberPrice?: number
  useLuxPlusPrice: boolean
}

interface Props {
  offer?: App.AnyOffer
  images: Array<App.Image>
  interstitials?: Array<ImageCarouselInterstitial>
  defaultIndex?: number
  onImageChange?: (index: number, image?: App.Image) => void
  initialView?: 'grid' | 'single'
}

function getImageKey(image: App.Image, index: number) {
  return image.id ?? image.url ?? index.toString()
}

function dedupeImages(images: Array<App.Image>) {
  const seen = new Set<string>()
  return images.filter((image, index) => {
    const key = getImageKey(image, index)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function formatRoomLabel(label: string) {
  return label
    .replace(/\s*[^\p{L}\p{N}\s]+\s*/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function isPropertyRoomForGallery(room: App.Property.Room | App.VillaRoom): room is App.Property.Room {
  return (
    'id' in room
    && 'images' in room
    && typeof room.id === 'string'
    && Array.isArray(room.images)
  )
}

function getPropertyRoomsForGallery(offer?: App.AnyOffer): Array<App.Property.Room> {
  if (!offer || !('property' in offer) || !offer.property || !('rooms' in offer.property)) {
    return EmptyArray
  }
  const rawRooms = offer.property.rooms
  const resolvedRooms: Array<App.Property.Room> = []
  if (Array.isArray(rawRooms)) {
    rawRooms.forEach((room) => {
      if (isPropertyRoomForGallery(room)) {
        resolvedRooms.push(room)
      }
    })
    return resolvedRooms
  }
  if (rawRooms && typeof rawRooms === 'object') {
    Object.values(rawRooms).forEach((room) => {
      if (isPropertyRoomForGallery(room as App.Property.Room | App.VillaRoom)) {
        resolvedRooms.push(room as App.Property.Room)
      }
    })
  }
  return resolvedRooms
}

function isBedbankOfferDetail(
  offer?: App.BedbankOffer | App.BedbankOfferSummary,
): offer is App.BedbankOffer {
  return !!offer && 'facilityGroups' in offer
}

function getCarouselIndexFromImageIndex(
  imageIndex: number,
  images: Array<App.Image>,
  interstitials: Array<{ position: number | 'start' | 'end' }>,
) {
  if (!interstitials.length) return imageIndex
  const sortedPositions = interstitials
    .map((interstitial) => {
      if (interstitial.position === 'start') return 0
      if (interstitial.position === 'end') return images.length
      return interstitial.position - 1
    })
    .sort((a, b) => a - b)
  let carouselIndex = imageIndex
  sortedPositions.forEach((pos) => {
    if (pos <= carouselIndex) {
      carouselIndex += 1
    }
  })
  return carouselIndex
}

function getOfferPropertyName(offer?: App.AnyOffer) {
  if (!offer) return ''
  if ('property' in offer && offer.property?.name) return offer.property.name
  if ('name' in offer && typeof offer.name === 'string') return offer.name
  return ''
}

function getPlainTextDescription(description?: string) {
  if (!description) return undefined
  if (BEDBANK_RICH_DESC_REGEX.test(description)) return undefined
  return description
}

function OfferGallerySectionedLightbox(props: Props) {
  const {
    offer,
    images,
    interstitials = EmptyArray,
    defaultIndex = 0,
    onImageChange,
    initialView = 'grid',
  } = props

  const theme = useTheme()
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const offerPageDispatch = useOfferPageDispatch()
  const modalElement = useModalElementContext()
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const canShowPricing = useAppSelector(hasPassedHidePricing)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const sectionRefs = useRef(new Map<string, HTMLElement>())

  const initialViewMode: ViewMode = initialView === 'single' ? 'single' : 'sections'
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const [activeSectionId, setActiveSectionId] = useState<string>(PROPERTY_SECTION_ID)
  const [currentSectionId, setCurrentSectionId] = useState<string>(PROPERTY_SECTION_ID)
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(defaultIndex)
  const [pendingRoomScrollId, setPendingRoomScrollId] = useState<string | undefined>(undefined)
  const [scrollAfterCloseRoomId, setScrollAfterCloseRoomId] = useState<string | undefined>(undefined)

  const photoCountLabel = useCallback((count: number) => (
    intl.formatMessage({
      id: 'PKdoGd97yqH4D7dniE38',
      defaultMessage: '{count, plural, one {# photo} other {# photos}}',
      description: 'Photo count label for gallery sections in the image gallery or photo section',
    }, { count })
  ), [intl])

  const safeOfferPageState = useMemo(() => {
    if (offerPageState.offer || !offer) return offerPageState
    if (isLEHotel(offer) || isBundleOffer(offer) || isVillaOffer(offer)) {
      return {
        ...offerPageState,
        offer,
      }
    }
    return offerPageState
  }, [offer, offerPageState])

  const selectCombinedRatesByRoom = useCallback(
    (state: App.State) => getPropertyCombinedRatesByRoomIfOffer(safeOfferPageState, state),
    [safeOfferPageState],
  )
  const combinedRatesByRoom = useAppSelector(selectCombinedRatesByRoom)

  const durationPackageOptions = useAppSelector(useCallback(
    (state: App.State) => (
      safeOfferPageState.offer
        ? getDurationPackageOptions(safeOfferPageState, state)
        : EmptyArray as ReturnType<typeof getDurationPackageOptions>
    ),
    [safeOfferPageState],
  ))

  const bedbankOfferForPricing = useMemo(() => {
    if (isBedbankOffer(offer) && isBedbankOfferDetail(offer)) {
      return offer
    }
    return offerPageState.bedbankOffer
  }, [offer, offerPageState.bedbankOffer])
  const [bedbankPackageViews] = useBedbankPackageViews(bedbankOfferForPricing)

  const usePropertyRoomOrder = useMemo(
    () => !isFlashHotelOffer(offer) && combinedRatesByRoom.length > 0,
    [combinedRatesByRoom.length, offer],
  )

  const roomSortOrder = useMemo(() => {
    if (!durationPackageOptions.length || usePropertyRoomOrder) return new Map<string, number>()
    const orderedRooms = isFlashHotelOffer(offer)
      ? buildRoomPackageOptionsViewOrder(durationPackageOptions)
      : buildRoomPackageOptionsViewOrderLPC(durationPackageOptions)
    return new Map(orderedRooms.map((room, index) => [room.roomId, index]))
  }, [durationPackageOptions, offer, usePropertyRoomOrder])

  const bedbankRoomSortOrder = useMemo(() => {
    if (!bedbankPackageViews.length) return new Map<string, number>()
    const sortedViews = [...bedbankPackageViews]
      .map((view, index) => {
        const primaryRate = view.cheapestRate
          ?? view.effectiveRates?.[0]
          ?? view.rates?.[0]
          ?? view.bundleRates?.[0]
        const propertyFees = primaryRate?.totals.propertyFees ?? 0
        const basePrice = (primaryRate?.totals.inclusive ?? 0) + propertyFees
        const sellPrice = view.package.sell?.price ?? 0
        const sellFees = view.package.sell?.propertyFees ?? 0
        const fallbackPrice = sellPrice ? sellPrice + sellFees : 0
        const resolvedPrice = basePrice || fallbackPrice
        return {
          roomId: view.package.roomId,
          resolvedPrice,
          index,
        }
      })
      .filter((view) => !!view.roomId && !!view.resolvedPrice)
      .toSorted((a, b) => {
        if (a.resolvedPrice !== b.resolvedPrice) return a.resolvedPrice - b.resolvedPrice
        return a.index - b.index
      })
    return new Map(sortedViews.map((view, index) => [view.roomId, index]))
  }, [bedbankPackageViews])

  const resolvedRoomSortOrder = useMemo(() => {
    if (roomSortOrder.size) return roomSortOrder
    if (bedbankRoomSortOrder.size) return bedbankRoomSortOrder
    return roomSortOrder
  }, [bedbankRoomSortOrder, roomSortOrder])

  const hasDatesForGallery = useMemo(() => (
    !!(offerPageState.checkInDate && offerPageState.checkOutDate)
  ), [offerPageState.checkInDate, offerPageState.checkOutDate])

  const roomPackageOptions = useAppSelector(useCallback(
    (state: App.State) => (
      safeOfferPageState.offer
        ? getPropertyRoomOptions(safeOfferPageState, state)
        : EmptyArray as ReturnType<typeof getPropertyRoomOptions>
    ),
    [safeOfferPageState],
  ))

  const availablePackageOptions = useAppSelector(useCallback(
    (state: App.State) => (
      safeOfferPageState.offer
        ? getAvailableDurationPackageOptions(safeOfferPageState, state)
        : EmptyArray as ReturnType<typeof getAvailableDurationPackageOptions>
    ),
    [safeOfferPageState],
  ))

  const availableRoomIds = useMemo(() => {
    if (!hasDatesForGallery) return undefined
    if (!availablePackageOptions.length && !bedbankPackageViews.length && !combinedRatesByRoom.length) return undefined
    const ids = new Set<string>()
    if (usePropertyRoomOrder) {
      combinedRatesByRoom.forEach(({ roomId, datesAvailable, roomFitsCapacity }) => {
        if (roomId && datesAvailable && roomFitsCapacity) {
          ids.add(roomId)
        }
      })
      return ids
    }
    availablePackageOptions.forEach((option) => {
      const roomId = option.package.roomType?.id ?? ('roomId' in option.package ? option.package.roomId : undefined)
      if (roomId && option.hotelAvailable && option.capacityValid) {
        ids.add(roomId)
      }
    })
    bedbankPackageViews.forEach((view) => {
      if (view.package.roomId && view.available && view.capacityValid) {
        ids.add(view.package.roomId)
      }
    })
    return ids
  }, [availablePackageOptions, bedbankPackageViews, combinedRatesByRoom, hasDatesForGallery, usePropertyRoomOrder])

  const effectiveOffer = useMemo(() => {
    const offerStateOffer = safeOfferPageState.offer
    const bedbankOffer = safeOfferPageState.bedbankOffer
    if (offer && offerStateOffer?.id === offer.id && 'packages' in offerStateOffer && offerStateOffer.packages?.length) {
      return offerStateOffer
    }
    if (offer && bedbankOffer?.id === offer.id) {
      return bedbankOffer
    }
    return offer
  }, [offer, safeOfferPageState.bedbankOffer, safeOfferPageState.offer])

  const roomSections = useMemo<Array<RoomSection>>(() => {
    if (!effectiveOffer) return EmptyArray
    const propertyRooms = getPropertyRoomsForGallery(effectiveOffer)
    const sections = new Map<string, RoomSection>()
    const addRoom = (id: string | undefined, name: string | undefined, roomImages: Array<App.Image> | undefined) => {
      if (!id || !name || !roomImages?.length) return
      if (availableRoomIds && !availableRoomIds.has(id)) return
      if (sections.has(id)) return
      sections.set(id, {
        id,
        name: formatRoomLabel(name),
        images: dedupeImages(roomImages),
        thumbnail: roomImages[0],
      })
    }

    if (usePropertyRoomOrder) {
      combinedRatesByRoom.forEach(({ propertyRoom }) => {
        addRoom(propertyRoom.id, propertyRoom.name, propertyRoom.images)
      })
      const orderedPropertyRooms = Array.from(sections.values())
      return orderedPropertyRooms
    }

    if ('packages' in effectiveOffer && effectiveOffer.packages) {
      effectiveOffer.packages.forEach((pkg) => {
        if ('roomId' in pkg && 'images' in pkg) {
          addRoom(pkg.roomId, pkg.name, pkg.images)
          return
        }
        if ('roomType' in pkg && pkg.roomType) {
          addRoom(pkg.roomType.id, pkg.roomType.name, pkg.roomType.images)
        }
      })
    }

    if (propertyRooms.length) {
      propertyRooms.forEach((room) => {
        addRoom(room.id, room.name, room.images)
      })
    }

    const unorderedSections = Array.from(sections.values())
    if (!resolvedRoomSortOrder.size) {
      return unorderedSections
    }
    const orderedSections = unorderedSections
      .map((section, index) => ({
        section,
        index,
        orderIndex: resolvedRoomSortOrder.get(section.id),
      }))
      .toSorted((a, b) => {
        const aOrder = a.orderIndex ?? Number.MAX_SAFE_INTEGER
        const bOrder = b.orderIndex ?? Number.MAX_SAFE_INTEGER
        if (aOrder !== bOrder) return aOrder - bOrder
        return a.index - b.index
      })
      .map(({ section }) => section)
    return orderedSections
  }, [availableRoomIds, combinedRatesByRoom, effectiveOffer, resolvedRoomSortOrder, usePropertyRoomOrder])

  const convertToDisplay = useMemo(
    () => getPriceDisplayFunction(offerPageState.priceMode, offerPageState.rooms),
    [offerPageState.priceMode, offerPageState.rooms],
  )

  const fromLabel = useMemo(() => (
    intl.formatMessage({
      id: 'u_lSo2D5qpg9NWPjf7p3',
      defaultMessage: 'From',
      description: 'Prefix for room price in gallery overlay when no dates selected',
    })
  ), [intl])

  const roomOverlayDetailsById = useMemo(() => {
    const detailsById = new Map<string, RoomOverlayDetails>()
    const setRoomDetails = (roomId?: string, name?: string, description?: string) => {
      if (!roomId || !name) return
      const existing = detailsById.get(roomId)
      detailsById.set(roomId, {
        name: existing?.name ?? formatRoomLabel(name),
        description: existing?.description ?? description,
        price: existing?.price,
        memberPrice: existing?.memberPrice,
        useLuxPlusPrice: existing?.useLuxPlusPrice ?? false,
      })
    }

    if (usePropertyRoomOrder) {
      combinedRatesByRoom.forEach(({ propertyRoom }) => {
        setRoomDetails(propertyRoom.id, propertyRoom.name, propertyRoom.description ?? undefined)
      })
    }

    if (offer && 'packages' in offer && offer.packages) {
      offer.packages.forEach((pkg) => {
        if ('roomType' in pkg && pkg.roomType) {
          setRoomDetails(pkg.roomType.id, pkg.roomType.name, pkg.roomType.description)
        } else if ('roomId' in pkg) {
          setRoomDetails(pkg.roomId, pkg.name)
        }
      })
    }

    getPropertyRoomsForGallery(offer).forEach((room) => {
      setRoomDetails(room.id, room.name, room.description)
    })

    if (offer && 'packages' in offer && offer.packages) {
      offer.packages.forEach((pkg) => {
        if ('roomId' in pkg && typeof pkg.description === 'string') {
          const plainDescription = getPlainTextDescription(pkg.description)
          if (plainDescription) {
            setRoomDetails(pkg.roomId, pkg.name, plainDescription)
          }
        }
      })
    }

    if (canShowPricing) {
      if (usePropertyRoomOrder) {
        combinedRatesByRoom.forEach(({ roomId, rates, propertyRoom }) => {
          if (!rates.length) return
          const cheapestBaseRate = min(
            rates,
            rate => (rate.totals.displayPrice > 0 ? rate.totals.displayPrice : Number.MAX_SAFE_INTEGER),
          )
          const cheapestMemberRate = min(
            rates,
            rate => (rate.luxPlusTotals?.displayPrice && rate.luxPlusTotals.displayPrice > 0
              ? rate.luxPlusTotals.displayPrice
              : Number.MAX_SAFE_INTEGER),
          )
          const cheapestBasePrice = cheapestBaseRate?.totals.displayPrice ?? 0
          const cheapestMemberPrice = cheapestMemberRate?.luxPlusTotals?.displayPrice ?? 0
          if (!cheapestBasePrice) return
          const useLuxPlusPrice = !!offer?.luxPlus && cheapestMemberPrice > 0 && cheapestMemberPrice < cheapestBasePrice
          const existing = detailsById.get(roomId)
          detailsById.set(roomId, {
            name: existing?.name ?? formatRoomLabel(propertyRoom.name),
            description: existing?.description,
            price: cheapestBasePrice,
            memberPrice: cheapestMemberPrice || existing?.memberPrice,
            useLuxPlusPrice,
          })
        })
      }
      roomPackageOptions.forEach((option) => {
        const roomId = option.package.roomType?.id
        if (!roomId) return
        const basePrice = option.pricing?.price ?? option.hotelTotal ?? 0
        const memberPrice = option.memberPricing?.price ?? option.hotelMemberTotal ?? 0
        if (!basePrice || basePrice <= 0) return
        const useLuxPlusPrice = !!offer?.luxPlus && memberPrice > 0 && memberPrice < basePrice
        const existing = detailsById.get(roomId)
        detailsById.set(roomId, {
          name: existing?.name ?? formatRoomLabel(option.package.roomType?.name ?? ''),
          description: existing?.description,
          price: basePrice,
          memberPrice: memberPrice || existing?.memberPrice,
          useLuxPlusPrice,
        })
      })
      if (bedbankPackageViews.length) {
        bedbankPackageViews.forEach((view) => {
          const roomId = view.package.roomId
          const primaryRate = view.cheapestRate
            ?? view.effectiveRates?.[0]
            ?? view.rates?.[0]
            ?? view.bundleRates?.[0]
          const propertyFees = primaryRate?.totals.propertyFees ?? 0
          const basePrice = (primaryRate?.totals.inclusive ?? 0) + propertyFees
          const memberInclusive = primaryRate?.totals.memberInclusive ?? 0
          const memberPrice = memberInclusive > 0 ? memberInclusive + propertyFees : 0
          const sellPrice = view.package.sell?.price ?? 0
          const sellFees = view.package.sell?.propertyFees ?? 0
          const fallbackPrice = sellPrice ? sellPrice + sellFees : 0
          if (!roomId) return
          const resolvedPrice = basePrice || fallbackPrice
          if (!resolvedPrice) return
          const useLuxPlusPrice = !!bedbankOfferForPricing?.luxPlus && memberPrice > 0 && memberPrice < resolvedPrice
          const existing = detailsById.get(roomId)
          detailsById.set(roomId, {
            name: existing?.name ?? formatRoomLabel(view.package.name),
            description: existing?.description,
            price: resolvedPrice,
            memberPrice: memberPrice || existing?.memberPrice,
            useLuxPlusPrice,
          })
        })
      }
    }

    return detailsById
  }, [
    bedbankOfferForPricing?.luxPlus,
    bedbankPackageViews,
    canShowPricing,
    combinedRatesByRoom,
    offer,
    roomPackageOptions,
    usePropertyRoomOrder,
  ])

  const propertySection = useMemo((): GallerySection => {
    const propertyImages = dedupeImages(images)
    return {
      id: PROPERTY_SECTION_ID,
      navLabel: intl.formatMessage({
        id: 'f0xtmIof625n4nP84wUy',
        defaultMessage: 'Property Highlights',
        description: 'Sidebar label for property highlights section on the offer detail page',
      }),
      heading: intl.formatMessage({
        id: 'VWnsWRpVRMlaYyYoMVgO',
        defaultMessage: 'Property Highlights',
        description: 'Heading for offer-level gallery images in the image gallery or photo section',
      }),
      images: propertyImages,
      thumbnail: propertyImages[0],
      type: 'property',
      countLabel: photoCountLabel(propertyImages.length),
    }
  }, [images, intl, photoCountLabel])

  const sections = useMemo<Array<GallerySection>>(() => {
    const roomItems = roomSections.map((room) => ({
      id: room.id,
      navLabel: room.name,
      heading: room.name,
      images: room.images,
      thumbnail: room.thumbnail,
      type: 'room' as const,
      largeImage: room.images[0],
      smallImages: room.images.slice(1),
      countLabel: photoCountLabel(room.images.length),
    }))
    if (!propertySection.images.length) {
      return roomItems
    }
    return [propertySection, ...roomItems]
  }, [photoCountLabel, propertySection, roomSections])

  const hasRoomSections = roomSections.length > 0
  const hasPropertyImages = images.length > 0
  const isLoading = useMemo(
    () => !hasPropertyImages && !hasRoomSections,
    [hasPropertyImages, hasRoomSections],
  )

  const contentSkeletonItems = useMemo(() => (
    Array.from({ length: 6 }, (_, index) => index)
  ), [])

  const shouldShowRoomSectionSkeletons = useMemo(() => (
    !hasRoomSections && hasPropertyImages && !availableRoomIds
  ), [availableRoomIds, hasPropertyImages, hasRoomSections])

  useEffect(() => {
    if (!sections.length) return
    const firstSection = sections[0]
    if (!sections.find(section => section.id === activeSectionId)) {
      setActiveSectionId(firstSection.id)
    }
    if (!sections.find(section => section.id === currentSectionId)) {
      setCurrentSectionId(firstSection.id)
    }
  }, [activeSectionId, currentSectionId, sections])

  const currentSection = useMemo(() => {
    return sections.find(section => section.id === currentSectionId) ?? sections[0]
  }, [currentSectionId, sections])

  const currentImages = currentSection?.images ?? EmptyArray
  const currentInterstitials = useMemo(
    () => (currentSection?.type === 'property' ? interstitials : EmptyArray),
    [currentSection?.type, interstitials],
  )
  const videoMeta = useMemo(() => {
    const firstVideo = currentInterstitials.find(i => i.id.startsWith('video-'))
    const videoId = firstVideo?.id.replace('video-', '')
    const videoPosition = typeof firstVideo?.position === 'number' ? firstVideo.position : undefined
    return {
      videoId,
      videoPosition,
    }
  }, [currentInterstitials])

  const galleryCounter = useMemo(() => {
    const totalCount = currentImages.length + currentInterstitials.length
    if (totalCount === 0) return '0 / 0'
    return `${currentImageIndex + 1} / ${totalCount}`
  }, [currentImages.length, currentImageIndex, currentInterstitials.length])

  const currentImage = useMemo(
    () => getImageAtCarouselIndex(currentImageIndex, currentImages, currentInterstitials),
    [currentImageIndex, currentImages, currentInterstitials],
  )

  useEffect(() => {
    if (viewMode !== 'single') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        carouselRef.current?.previousImage()
      } else if (event.key === 'ArrowRight') {
        carouselRef.current?.nextImage()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [viewMode])

  useEffect(() => {
    if (!currentImages.length && !currentInterstitials.length) return
    const image = getImageAtCarouselIndex(currentImageIndex, currentImages, currentInterstitials)
    setCurrentImageIndex((prevIndex) => {
      if (prevIndex >= currentImages.length + currentInterstitials.length) {
        return 0
      }
      return prevIndex
    })
    if (!image) {
      const fallbackImage = getImageAtCarouselIndex(0, currentImages, currentInterstitials)
      if (fallbackImage) {
        setCurrentImageIndex(0)
      }
    }
  }, [currentImages, currentImageIndex, currentInterstitials])

  const handleNavClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const nextSectionId = event.currentTarget.dataset.sectionId ?? PROPERTY_SECTION_ID
    setActiveSectionId(nextSectionId)
    setViewMode('sections')
    const sectionNode = sectionRefs.current.get(nextSectionId)
    if (sectionNode) {
      sectionNode.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const handleImageClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const nextSectionId = event.currentTarget.dataset.sectionId ?? PROPERTY_SECTION_ID
    const nextIndex = Number(event.currentTarget.dataset.imageIndex ?? '0')
    setActiveSectionId(nextSectionId)
    setCurrentSectionId(nextSectionId)
    setCurrentImageIndex(nextIndex)
    setViewMode('single')
    carouselRef.current?.goToIndex(nextIndex, 'instant')
  }, [])

  const handleBackToSections = useCallback(() => {
    setViewMode('sections')
  }, [])

  const handlePrevClick = useCallback(() => {
    carouselRef.current?.previousImage()
  }, [])

  const handleNextClick = useCallback(() => {
    carouselRef.current?.nextImage()
  }, [])

  const handleChangeImage = useCallback((index: number, image?: App.Image) => {
    onImageChange?.(index, image)
    setCurrentImageIndex(index)
  }, [onImageChange])

  const handleThumbnailSelect = useCallback((index: number) => {
    carouselRef.current?.goToIndex(index, 'instant')
  }, [])

  const hasDates = useMemo(() => (
    !!(offerPageState.checkInDate && offerPageState.checkOutDate)
  ), [offerPageState.checkInDate, offerPageState.checkOutDate])

  const availabilityCtaLabel = useMemo(() => {
    if (hasDates) {
      return intl.formatMessage({
        id: '8s2WxRhc5IWV5RJLMQQA',
        defaultMessage: 'View all packages',
        description: 'CTA label to view package options from the gallery modal',
      })
    }
    return intl.formatMessage({
      id: 'iiIX-xcvMw4EvVJL509a',
      defaultMessage: 'Check availability',
      description: 'CTA label to check availability from the gallery modal',
    })
  }, [hasDates, intl])

  const invalidRoomSelection = useMemo(() => (
    !hasSelectedRooms(offerPageState.rooms) || isRoomsInvalid(offerPageState.rooms)
  ), [offerPageState.rooms])

  const canShowAvailabilityCta = useMemo(() => (
    isFlashHotelOffer(offer) || isLpcHotelOffer(offer) || isBedbankOffer(offer)
  ), [offer])

  const closeModal = useCallback(() => {
    modalElement?.resolve()
  }, [modalElement])

  const scrollToPackageOptions = useCallback(() => {
    const anchor = document.getElementById('Package-Options')
    if (!anchor) return
    requestAnimationFrame(() => {
      anchor.scrollIntoView({ behavior: 'auto' })
    })
  }, [])

  const scrollToRoom = useCallback((roomId: string) => {
    const anchors = Array.from(document.querySelectorAll<HTMLElement>(`#${CSS.escape(roomId)}`))
    const packageOptionsAnchor = document.getElementById('Package-Options')
    const visibleAnchors = anchors.filter(anchor => anchor.getClientRects().length > 0)
    const anchor = (
      visibleAnchors.find(a => packageOptionsAnchor?.contains(a))
      ?? visibleAnchors[0]
      ?? anchors.find(a => packageOptionsAnchor?.contains(a))
      ?? anchors[0]
      ?? null
    )
    if (!anchor) {
      scrollToPackageOptions()
      return
    }
    requestAnimationFrame(() => {
      anchor.scrollIntoView({ behavior: 'auto' })
    })
  }, [scrollToPackageOptions])

  const handleViewRoomClick = useCallback(() => {
    if (currentSection?.type !== 'room') return
    if (!isLoggedIn) {
      setPendingRoomScrollId(currentSection.id)
      dispatch(accountAccessShowLogin())
      return
    }
    setScrollAfterCloseRoomId(currentSection.id)
    closeModal()
  }, [closeModal, currentSection, dispatch, isLoggedIn])

  const openAvailabilityFilters = useCallback(() => {
    const filter = (invalidRoomSelection || !offerPageState.durationSelected) ? 'capacity' : 'checkin'
    offerPageDispatch({
      type: OfferPageActions.checkAvailability,
      filter,
      flights: offerPageState.flights,
    })
  }, [invalidRoomSelection, offerPageDispatch, offerPageState.durationSelected, offerPageState.flights])

  const handleAvailabilityCtaClick = useCallback(() => {
    closeModal()
    if (!hasDates) {
      if (!isLoggedIn) {
        dispatch(accountAccessShowLogin())
        return
      }
      openAvailabilityFilters()
      return
    }
    scrollToPackageOptions()
  }, [
    closeModal,
    dispatch,
    hasDates,
    isLoggedIn,
    openAvailabilityFilters,
    scrollToPackageOptions,
  ])

  useEffect(() => {
    if (!pendingRoomScrollId || !isLoggedIn) return
    setScrollAfterCloseRoomId(pendingRoomScrollId)
    closeModal()
    setPendingRoomScrollId(undefined)
  }, [closeModal, isLoggedIn, pendingRoomScrollId])

  const handleModalAfterClose = useCallback(() => {
    if (!scrollAfterCloseRoomId) return
    scrollToRoom(scrollAfterCloseRoomId)
    setScrollAfterCloseRoomId(undefined)
  }, [scrollAfterCloseRoomId, scrollToRoom])

  const viewRoomCtaLabel = useMemo(() => (
    intl.formatMessage({
      id: 'hijiMiEnoGOPIiXbJaQE',
      defaultMessage: 'View room',
      description: 'CTA label to scroll to the selected room from the gallery modal.',
    })
  ), [intl])

  const modalTitle = useMemo(() => {
    if (viewMode === 'single' && currentSection) {
      return currentSection.heading
    }
    return intl.formatMessage({
      id: 'KQ4lbLtqHklrHc29Ycdg',
      defaultMessage: 'Gallery',
      description: 'Modal title for the offer gallery in the image gallery or photo section',
    })
  }, [intl, viewMode, currentSection])

  const modalSubtitle = useMemo(() => getOfferPropertyName(offer), [offer])

  const renderContentSkeletonItem = useCallback((index: number) => (
    <SkeletonGridItem key={`content-skeleton-${index}`} />
  ), [])

  const contentSkeletonNodes = useMemo(() => (
    contentSkeletonItems.map(renderContentSkeletonItem)
  ), [contentSkeletonItems, renderContentSkeletonItem])

  const roomSectionSkeleton = useMemo(() => (
    <Section key="room-section-skeleton">
      <SectionSkeletonHeader>
        <SkeletonLine />
        <SkeletonLine />
      </SectionSkeletonHeader>
      <PropertyGrid>
        {contentSkeletonNodes}
      </PropertyGrid>
    </Section>
  ), [contentSkeletonNodes])

  const renderRoomSectionImages = useCallback((section: GallerySection) => {
    if (!section.largeImage) return null
    const overlayDetails = roomOverlayDetailsById.get(section.id)
    const overlayName = overlayDetails?.name ?? section.heading
    const showLuxPlusPrice = !!overlayDetails?.useLuxPlusPrice
    const hasDatesSelected = !!offerPageState.checkInDate
    const allowPriceWithoutDates = isFlashHotelOffer(offer)
    const canShowOverlayPrice = hasDatesSelected || allowPriceWithoutDates
    const showFromLabel = !hasDatesSelected || (showLuxPlusPrice && !canViewLuxPlusBenefits)
    const overlayPrice = overlayDetails?.price && overlayDetails.price > 0
      ? convertToDisplay(overlayDetails.price)
      : undefined
    const overlayMemberPrice = overlayDetails?.memberPrice ? convertToDisplay(overlayDetails.memberPrice) : undefined
    return (
      <RoomGroup>
        <RoomLargeImageButton
          data-section-id={section.id}
          data-image-index={0}
          onClick={handleImageClick}
        >
          {section.largeImage.id && <ResponsiveImage id={section.largeImage.id} fit="center" />}
          {section.largeImage.url && <Image image={section.largeImage} fit="center" />}
          <RoomOverlay>
            <RoomOverlayTitle variant="heading4">{overlayName}</RoomOverlayTitle>
            {overlayDetails?.description && (
              <RoomOverlayDescription variant="medium">{overlayDetails.description}</RoomOverlayDescription>
            )}
            {overlayPrice && canShowOverlayPrice && (
              <>
                {showFromLabel && <RoomOverlayPriceCaption>{fromLabel}</RoomOverlayPriceCaption>}
                <RoomOverlayPriceStackWrapper className={getThemeClassName({ mobile: 'inverse' })}>
                  <LuxPlusPriceStack
                    size="S"
                    price={overlayPrice}
                    memberPrice={overlayMemberPrice}
                    saleUnit={offerPageState.priceMode === 'perPerson' ? 'person' : 'total'}
                    offerConfig={offer?.luxPlus}
                    mode={showLuxPlusPrice ? 'regular-prominent' : undefined}
                  />
                </RoomOverlayPriceStackWrapper>
              </>
            )}
          </RoomOverlay>
        </RoomLargeImageButton>
        {section.smallImages && section.smallImages.length > 0 && (
          <RoomSmallRow>
            {section.smallImages.map((image, index) => (
              <PropertyImageButton
                key={getImageKey(image, index)}
                data-section-id={section.id}
                data-image-index={index + 1}
                onClick={handleImageClick}
              >
                {image.id && <ResponsiveImage id={image.id} fit="center" />}
                {image.url && <Image image={image} fit="center" />}
              </PropertyImageButton>
            ))}
          </RoomSmallRow>
        )}
      </RoomGroup>
    )
  }, [
    canViewLuxPlusBenefits,
    convertToDisplay,
    fromLabel,
    handleImageClick,
    offer,
    offerPageState.checkInDate,
    offerPageState.priceMode,
    roomOverlayDetailsById,
  ])

  const renderSection = useCallback((section: GallerySection) => {
    const isProperty = section.type === 'property'
    return (
      <Section
        key={section.id}
        id={section.id}
        ref={(node) => {
          if (node) {
            sectionRefs.current.set(section.id, node)
          }
        }}
      >
        <SectionHeader>
          <Heading variant="heading3">{section.heading}</Heading>
          <SectionCount variant="medium">{section.countLabel}</SectionCount>
        </SectionHeader>
        {isProperty && (
          <PropertyGrid>
            <OfferGalleryPropertyImages
              section={section}
              interstitials={interstitials}
              onImageClick={handleImageClick}
              getImageKey={getImageKey}
              getCarouselIndexFromImageIndex={getCarouselIndexFromImageIndex}
            />
          </PropertyGrid>
        )}
        {!isProperty && renderRoomSectionImages(section)}
      </Section>
    )
  }, [handleImageClick, interstitials, renderRoomSectionImages])

  const sectionNodes = useMemo(() => (
    sections.map(renderSection)
  ), [renderSection, sections])

  const contentNodes = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <SectionSkeletonHeader>
            <SkeletonLine />
            <SkeletonLine />
          </SectionSkeletonHeader>
          <PropertyGrid>
            {contentSkeletonNodes}
          </PropertyGrid>
        </>
      )
    }

    if (shouldShowRoomSectionSkeletons) {
      return (
        <>
          {sectionNodes}
          {roomSectionSkeleton}
        </>
      )
    }

    return sectionNodes
  }, [contentSkeletonNodes, isLoading, roomSectionSkeleton, sectionNodes, shouldShowRoomSectionSkeletons])

  const videoIds = useMemo(() => (
    videoMeta.videoId ? [videoMeta.videoId] : EmptyArray
  ), [videoMeta.videoId])

  return (
    <SectionedGalleryModal
      size="XL"
      height="full"
      onAfterClose={handleModalAfterClose}
    >
      <SectionedGalleryWrapper>
        <ModalHeader
          title={modalTitle}
          subtitle={modalSubtitle}
          onBackButtonClick={viewMode === 'single' ? handleBackToSections : undefined}
        />
        {viewMode === 'sections' && (
          <SectionedGalleryLayout>
            <OfferGallerySectionedSidebar
              sections={sections}
              activeSectionId={activeSectionId}
              onNavClick={handleNavClick}
              isLoading={isLoading}
              shouldShowRoomSectionSkeletons={shouldShowRoomSectionSkeletons}
              canShowAvailabilityCta={canShowAvailabilityCta}
              availabilityCtaLabel={availabilityCtaLabel}
              onAvailabilityCtaClick={handleAvailabilityCtaClick}
            />
            <ContentArea>
              {contentNodes}
            </ContentArea>
          </SectionedGalleryLayout>
        )}
        {viewMode === 'single' && (
          <OfferGallerySectionedSingleView
            carouselRef={carouselRef}
            currentImageIndex={currentImageIndex}
            currentImageTitle={currentImage?.title}
            currentImages={currentImages}
            currentInterstitials={currentInterstitials}
            galleryCounter={galleryCounter}
            highlightColor={theme.palette.highlight.primary.normalBackground}
            onImageChange={handleChangeImage}
            onNext={handleNextClick}
            onPrev={handlePrevClick}
            onThumbnailSelect={handleThumbnailSelect}
            onViewRoomClick={handleViewRoomClick}
            showViewRoomCta={currentSection?.type === 'room'}
            viewRoomCtaLabel={viewRoomCtaLabel}
            videoIds={videoIds}
            videoPosition={videoMeta.videoPosition}
          />
        )}
      </SectionedGalleryWrapper>
    </SectionedGalleryModal>
  )
}

export default OfferGallerySectionedLightbox
